import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { styled } from '@mui/material/styles';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Paper,
  LinearProgress,
  Button,
  Badge,
  Stack,
} from '@mui/material';
import Tooltip, {tooltipClasses} from '@mui/material/Tooltip';
import moment from '@utils/moment';
import {leftPad} from '@utils/dateUtils';
import FiberNewIcon from '@mui/icons-material/FiberNew';

// import Emitter from '@utils/eventEmitter';
import Modal from '@views/board/modal/Modal';
import DynamicHead from '@components/DynamicHead';

import {
  getBoardContentList,
  getBoardContent,
  downloadBoardAttach,
} from '@store/board';

const HtmlTooltip = styled(({className, ...props}) => (
  <Tooltip {...props} classes={{popper: className}} />
))(({theme}) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: '#fff',
    color: 'rgba(0, 0, 0, 0.87)',
    maxWidth: 400,
    fontSize: theme.typography.pxToRem(12),
    border: '1px solid #dadde9',
    padding: 10,
  },
}));
const ItemTableCell = styled(TableCell)({
  '&.edit': {
    backgroundColor: '#ffe7e7',
    cursor: 'pointer',
    transition: '.3s background-color',
    '&:hover': {
      backgroundColor: '#fdbdbd',
    }
  }
});
const ContentBox = styled('div')({
  whiteSpace: 'pre-wrap',
  overflow: 'auto',
  maxHeight: 50,
  backgroundColor: '#f9f9f9',
  padding: 10,
});

const headerList = [
  {key: 'name', name: '일자', width: '10%'},
  {key: 'birth', name: '제목', width: '25%'},
  {key: 'companyName', name: '내용' , width: '45%'},
  {key: 'position', name: '파일'},
]

const BoardTable = ({
  boardId,
}) => {
  const dispatch = useDispatch();
  const {
    boardContentList,
    boardContentLists,
  } = useSelector(store => store.board);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalInfo, setModalInfo] = useState(null);

  const onOpenContentModal = async (data) => {
    const {contentId} = data;
    await dispatch(getBoardContent({contentId}));
    setModalInfo(boardContentLists[contentId]);
    setModalOpen(true);
  };

  const onCloseContentModal = () => {
    setModalInfo(null);
    setModalOpen(false);
  };

  const onDownloadFile = async (e, data) => {
    e.stopPropagation();
    const {name, attachId} = data
    dispatch(downloadBoardAttach({
      name,
      attachId,
    }));
  }

  const onGetBoardContentList = useCallback(async () => {
    if (!boardId) return;
    setLoading(true)
    await dispatch(getBoardContentList({boardId}));
    setLoading(false)
  }, [dispatch, boardId]);

  useEffect(() => onGetBoardContentList(), [onGetBoardContentList]);

  return (
    <>
      <TableContainer component={Paper} sx={{position: 'relative'}}>
        {loading && <LinearProgress sx={{position: 'absolute', width: '100%'}}/>}
        <Table sx={{ minWidth: 650 }} size="small">
          <DynamicHead 
            list={headerList}
            height={50}
          />
          <TableBody>
            {
              boardContentList && boardContentList.map(contentId => {
                const item = boardContentLists[contentId];
                return (
                  <BoardTableItem 
                    item={item} 
                    key={contentId}
                    openEditModal={onOpenContentModal}
                    onDownloadFile={onDownloadFile}
                  />
                );
              })
            }
          </TableBody>
        </Table>
      </TableContainer>

      {modalOpen && <Modal 
        data={modalInfo}
        open={modalOpen}
        boardId={boardId}
        closeModal={onCloseContentModal}
        onDownloadFile={onDownloadFile}
      />}
    </>
  )
}

const BoardTableItem = ({
  item,
  openEditModal,
  onDownloadFile,
}) => {

  const sortBoardAttachList = useMemo(() => {
    const sortList = [...item.boardAttachResList];
    sortList.sort(function (a, b) {
      if (a.sort > b.sort) {
        return 1;
      }
      if (a.sort < b.sort) {
        return -1;
      }
      // a must be equal to b
      return 0;
    });
    return sortList;
  }, [item]);

  const displayDate = useMemo(() => {
    const {createDateTime} = item;
    return `${createDateTime[0]}-${leftPad(createDateTime[1])}-${leftPad(createDateTime[2])}`;
  }, [item]);

  const isNew = useMemo(() => {
    const startDate = moment(new Date()).subtract(3, 'd');
    const endDate = moment(new Date());
    const targetDate = moment(displayDate);
    const range = moment.range(startDate, endDate);
    return range.contains(targetDate);
  }, [displayDate]);

  return (
    <TableRow onClick={() => openEditModal(item)} hover sx={{cursor: 'pointer', backgroundColor: isNew && '#fffcf6'}}>
      <ItemTableCell align="center">
        {displayDate}
      </ItemTableCell>
      <ItemTableCell>
        <Stack direction="row" spacing={.5} alignItems="center">
          <div>{item.title}</div>
          {isNew && <FiberNewIcon color={'warning'} />}
        </Stack>
      </ItemTableCell>
      <ItemTableCell>
        <ContentBox>{item.content}</ContentBox>
      </ItemTableCell>
      <ItemTableCell align="center">
        {!!item.boardAttachResList.length && <HtmlTooltip
          placement="top"
          title={
            <>
              {sortBoardAttachList.map((data, index) => {
                return <Tooltip title={data.name} key={index} placement="left">
                  <Button 
                    onClick={(e) => onDownloadFile(e, data)}
                    variant="outlined"
                    className="attachFile-btn"
                    sx={{marginBottom: '2px!important', marginTop: '2px!important'}}
                  >{data.name}</Button>
                </Tooltip>
              })}
            </>
          }
        >
          <Badge badgeContent={item.boardAttachResList.length} color="info">
            <Button 
              variant="outlined"
              className="attachFile-btn"
              onClick={(e) => onDownloadFile(e, sortBoardAttachList[0])}
            >{sortBoardAttachList[0].name}</Button>
          </Badge>
        </HtmlTooltip>}
      </ItemTableCell>
    </TableRow>
  )
}

export default BoardTable